import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/config';
import Constants from '../../utils/constants';

export const getAllApprovalTransactions = createAsyncThunk(
  'transactionApproval/getAll',
  async ({ page = 1, perPage = 10, ...filters }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, perPage });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // If value is an array, join with comma
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value);
          }
        }
      });
      
      const response = await axiosInstance.get(`/api/v1/transaction_approval?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch approval transactions');
    }
  }
);

export const updateTransactionApprovalStatus = createAsyncThunk(
  'transactionApproval/updateStatus',
  async ({ transactionId, approvalStatus, remarks }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/v1/transaction_approval/${transactionId}/status`, {
        approvalStatus,
        remarks: remarks || ''
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update approval status');
    }
  }
);

const transactionApprovalSlice = createSlice({
  name: 'transactionApproval',
  initialState: {
    pendingTransactions: [],
    historyTransactions: [],
    pendingPagination: {},
    historyPagination: {},
    loading: false,
    historyLoading: false,
    error: null,
    isUpdated: false
  },
  reducers: {
    clearFlags: (state) => {
      state.isUpdated = false;
      state.error = null;
    },
    appendHistoryTransactions: (state, action) => {
      state.historyTransactions = [...state.historyTransactions, ...(action.payload.approvalTransactions || [])];
      state.historyPagination = action.payload.pagination || {};
    },
    resetHistoryTransactions: (state) => {
      state.historyTransactions = [];
      state.historyPagination = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllApprovalTransactions.pending, (state, action) => {
        // Check if it's a history request (status is approved/rejected or array containing them, or empty for history)
        const status = action.meta.arg?.status;
        const statusArray = Array.isArray(status) ? status : [status];
        const hasHistoryStatus = statusArray.some(
          (s) => s === Constants.APPROVAL_STATUS.APPROVED || s === Constants.APPROVAL_STATUS.REJECTED
        );
        const isHistoryRequest = hasHistoryStatus || (status === '' && action.meta.arg?.isHistory);
        
        if (isHistoryRequest) {
          state.historyLoading = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(getAllApprovalTransactions.fulfilled, (state, action) => {
        const status = action.meta.arg?.status;
        const statusArray = Array.isArray(status) ? status : [status];
        const hasHistoryStatus = statusArray.some(
          (s) => s === Constants.APPROVAL_STATUS.APPROVED || s === Constants.APPROVAL_STATUS.REJECTED
        );
        const isHistoryRequest = hasHistoryStatus || (status === '' && action.meta.arg?.isHistory);
        const isAppend = action.meta.arg?.page > 1;

        if (isHistoryRequest) {
          state.historyLoading = false;
          if (isAppend) {
            // Append for infinite scroll
            state.historyTransactions = [...state.historyTransactions, ...(action.payload.approvalTransactions || [])];
          } else {
            // Replace for initial load or filter change
            state.historyTransactions = action.payload.approvalTransactions || [];
          }
          state.historyPagination = action.payload.pagination || {};
        } else {
          state.loading = false;
          state.pendingTransactions = action.payload.approvalTransactions || [];
          state.pendingPagination = action.payload.pagination || {};
        }
        state.error = null;
      })
      .addCase(getAllApprovalTransactions.rejected, (state, action) => {
        const status = action.meta.arg?.status;
        const statusArray = Array.isArray(status) ? status : [status];
        const hasHistoryStatus = statusArray.some(
          (s) => s === Constants.APPROVAL_STATUS.APPROVED || s === Constants.APPROVAL_STATUS.REJECTED
        );
        const isHistoryRequest = hasHistoryStatus || (status === '' && action.meta.arg?.isHistory);
        if (isHistoryRequest) {
          state.historyLoading = false;
        } else {
          state.loading = false;
        }
        state.error = action.payload;
      })
      .addCase(updateTransactionApprovalStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionApprovalStatus.fulfilled, (state) => {
        state.loading = false;
        state.isUpdated = true;
      })
      .addCase(updateTransactionApprovalStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearFlags, appendHistoryTransactions, resetHistoryTransactions } = transactionApprovalSlice.actions;
export default transactionApprovalSlice.reducer;
